// Repeatable product/qty/price line rows for a sales order's create/DRAFT-edit
// screens (BUILD_SPEC.md §5.7 sales_order_lines: many lines per order). Shared
// by SalesOrderNew.tsx and SalesOrderDetail.tsx's DRAFT editor so the two
// screens can never drift on line shape/validation — mirrors
// DispatchLinesEditor.tsx's presentational-only pattern (wire conversion stays
// the caller's job), extended with the unit-aware qty placeholder
// (`formatLineQty`'s sibling `qtyInputLabel`, see format.ts) that
// `salesOrderLineInputSchema` (packages/core/src/schemas/sales.ts) requires:
// an EACH product's line carries a whole-unit count, not a mass.
import { ActionIcon, Button, Group, Stack, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ProductSelect } from './ProductSelect';
import { randToCents } from '../format';
import type { ProductRow } from '../types';
import type { SalesOrderLineWriteInput } from '../api';

export interface SalesOrderLineFormRow {
  key: string;
  productId: string;
  productLabel: string;
  qty: string;
  priceRand: string;
}

export function emptySalesOrderLineRow(): SalesOrderLineFormRow {
  return { key: crypto.randomUUID(), productId: '', productLabel: '', qty: '', priceRand: '' };
}

/** True only when every line has a product, a positive quantity (a whole
 * number for an EACH product — echoes `positiveQtySchema`/the server's own
 * base_uom semantics client-side; the server remains the actual enforcement
 * point), and a price. Shared by both screens so "can I submit?" never drifts
 * from what `toSalesOrderLineInputs` below actually sends. */
export function salesOrderLinesValid(rows: SalesOrderLineFormRow[], productsById: Map<string, ProductRow>): boolean {
  if (rows.length === 0) return false;
  return rows.every((row) => {
    if (!row.productId || !row.priceRand.trim()) return false;
    const qty = Number(row.qty);
    if (!row.qty.trim() || !Number.isFinite(qty) || qty <= 0) return false;
    if (productsById.get(row.productId)?.baseUom === 'EACH' && !Number.isInteger(qty)) return false;
    return true;
  });
}

/** Wire-safe line inputs (`priceCents` as a decimal string — see api.ts's
 * "wire-safe write types" note) in submission order; `lineNo` is always
 * reassigned 1..N from the current row order, matching every other DRAFT-only
 * editable document in this app (dispatch/PO lines are the same "the array
 * order IS the lineNo order" convention). */
export function toSalesOrderLineInputs(rows: SalesOrderLineFormRow[]): SalesOrderLineWriteInput[] {
  return rows.map((row, index) => ({
    lineNo: index + 1,
    productId: row.productId,
    qty: row.qty.trim(),
    priceCents: randToCents(row.priceRand),
  }));
}

interface SalesOrderLinesEditorProps {
  rows: SalesOrderLineFormRow[];
  onChange: (rows: SalesOrderLineFormRow[]) => void;
  /** ALL products (active + inactive), keyed by id — resolves each row's
   * baseUom for the qty placeholder even for a since-deactivated product
   * already on the order. The picker itself (ProductSelect) is active-only. */
  productsById: Map<string, ProductRow>;
}

export function SalesOrderLinesEditor({ rows, onChange, productsById }: SalesOrderLinesEditorProps) {
  function updateRow(key: string, patch: Partial<SalesOrderLineFormRow>) {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function removeRow(key: string) {
    onChange(rows.filter((row) => row.key !== key));
  }

  function addRow() {
    onChange([...rows, emptySalesOrderLineRow()]);
  }

  return (
    <Stack gap="xs">
      <Table.ScrollContainer minWidth={640}>
      <Table verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            {/* Neutral header — an order can mix KG and EACH products, so each
                row's own placeholder ("kg" / "Units") carries the unit instead
                of a single table-wide header that would lie for one of them. */}
            <Table.Th w={140}>
              <Tooltip label="In the product's base unit — kg or count, depending on the product chosen." multiline w={220}>
                <span>Qty</span>
              </Tooltip>
            </Table.Th>
            <Table.Th w={140}>Price (R)</Table.Th>
            <Table.Th w={48} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => {
            const baseUom = productsById.get(row.productId)?.baseUom;
            return (
              <Table.Tr key={row.key}>
                <Table.Td>
                  <ProductSelect
                    value={row.productId}
                    onChange={(productId, productLabel) => updateRow(row.key, { productId, productLabel })}
                    aria-label="Product"
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label="Quantity"
                    placeholder={baseUom === 'EACH' ? 'Units' : 'kg'}
                    value={row.qty}
                    onChange={(e) => updateRow(row.key, { qty: e.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    aria-label="Price"
                    placeholder="0.00"
                    value={row.priceRand}
                    onChange={(e) => updateRow(row.key, { priceRand: e.currentTarget.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Remove this line from the order">
                    <ActionIcon color="red" variant="subtle" aria-label="Remove line" onClick={() => removeRow(row.key)} disabled={rows.length <= 1}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      <Group justify="space-between">
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addRow}>
          Add line
        </Button>
        {rows.length === 0 && (
          <Text size="sm" c="dimmed">
            At least one line is required.
          </Text>
        )}
      </Group>
    </Stack>
  );
}
